import type React from 'react'
import { useImperativeHandle, useRef } from 'react'

import type { CropZoomSliderRef } from '../types/crop-zoom-slider-props'

/**
 * Escape-cancel for the slider's thumb drag: exposes `cancelActiveDrag` on `ref` and returns the
 * guarded `onValueChange`/`onValueCommit` bodies, which forward to `apply` (with `commit` false /
 * true) unless the drag was cancelled.
 */
export function useCancellableSliderDrag(ref: React.ForwardedRef<CropZoomSliderRef>) {
  // True between the first `onValueChange` of a drag and its matching `onValueCommit` — lets
  // `cancelActiveDrag` know whether there's actually a gesture to cancel.
  const isDraggingRef = useRef(false)
  // Set by `cancelActiveDrag`; consumed (and reset) by the next `onValueCommit`. The physical
  // pointer drag Radix is tracking doesn't stop just because we stopped reacting to it, so this
  // suppresses every further `onValueChange` and skips the final `onValueCommit`'s persistence.
  const cancelledRef = useRef(false)

  useImperativeHandle(
    ref,
    () => ({
      cancelActiveDrag: () => {
        if (!isDraggingRef.current) return false

        cancelledRef.current = true

        return true
      },
    }),
    []
  )

  const handleValueChange = (value: number, apply: (value: number, commit: boolean) => void) => {
    if (cancelledRef.current) return
    isDraggingRef.current = true
    apply(value, false)
  }

  const handleValueCommit = (value: number, apply: (value: number, commit: boolean) => void) => {
    isDraggingRef.current = false
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }

    apply(value, true)
  }

  return { handleValueChange, handleValueCommit }
}
